import React from "react";
import { FormattedHTMLMessage } from "react-intl";
import store from "store";

import {
    availableLanguagesForTranslation,
    languages,
    translationLevelStatusLabels,
} from "../../../../common/parameters";
import { LoaderWrapper, Loader } from "../../../components/Loader";
import { InputTextArea, Select } from "../../../components/Form";
import routing from "../../../routing";

import TranslatorChat from "./TranslatorChat";
import TokenTable from "./TokenTable";

import { Block, BlockHeader, Caret, LevelName, StatusMarker, BlockContent, MergeButton } from "./styles";
import { IProps } from "./interfaces";

function downloadJSON(data, filename = "data.json") {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
}

class LanguagePage extends React.Component<IProps, any> {
    state = {
        users: [],
        tokenLevels: [],
        translationLevelStatus: [],
        isInitalFetching: true,
        errorInitialLoad: false,
        progress: [],
        chatCheckedAt: null,
        sourceLanguage: "en",
        uploadJSON: "",
    };
    eventSource: EventSource;
    chatCallback: (data: any) => void;
    componentDidMount() {
        this.getLevels();
        this.setState({
            sourceLanguage: store.get("sourceLanguage") || "en",
        });
        this.eventSource = new EventSource("/api/translation/broadcasting");
        this.eventSource.onmessage = this.handleBroadcasting;
    }
    setEventSourceCallback = (callback) => {
        this.chatCallback = callback;
    };
    componentWillUnmount() {
        this.eventSource.close();
    }
    setChatChecked = (chatCheckedAt: number) => {
        this.setState({ chatCheckedAt });
    };
    handleBroadcasting = (message: any) => {
        const { data } = JSON.parse(message.data);
        if (!data) return;
        if (data.user === this.props.user._id || data.language !== this.props.match.params.language) return;
        if (data.event === "edit-translation") {
            this.setState(({ tokenLevels }) => ({
                tokenLevels: tokenLevels.map(level => {
                    return {
                        ...level,
                        ...(level.tokens && {
                            tokens: level.tokens.map(token => ({
                                ...token,
                                translationToken: token._id === data._id ? data.token : token.translationToken,
                            })),
                        }),
                    };
                }),
            }));
        } else {
            if (this.chatCallback) {
                this.chatCallback(data);
            }
        }
    };
    goBack = () => {
        this.props.history.replace(`/${routing.translate}`);
    };
    getLevels = () => {
        const { language } = this.props.match.params;
        if (!language || availableLanguagesForTranslation.indexOf(language) === -1) {
            this.goBack();
        }
        this.props.get(
            `translation/get-translation-levels/${language}`,
            ({ data }) => {
                this.setState({
                    ...data,
                    isInitalFetching: false,
                });
            },
            error => {
                if ((error as any).response.status === 403) this.goBack();
            }
        );
    };
    getTokenLevel = loadingToken => {
        this.props.get(
            `translation/translation-level-tokens/${this.props.match.params.language}/${loadingToken._id}/${this.state.sourceLanguage}`,
            res => {
                const { data } = res;
                this.setState(({ tokenLevels }) => ({
                    tokenLevels: tokenLevels.map(level => {
                        if (level._id === loadingToken._id) {
                            return {
                                ...level,
                                tokens: data.tokens.map(token => ({
                                    ...token,
                                    translationToken:
                                        data.translationTokens.find(ttoken => ttoken.tokenId === token._id) || null,
                                })),
                                isFetching: false,
                            };
                        }
                        return level;
                    }),
                }));
            }
        );
    };
    toggleLevel = togglingLevel => {
        const { translationLevelStatus } = this.state;
        const status =
            (translationLevelStatus.find(status => status.level === togglingLevel._id) || { status: 0 }).status || 0;
        if (!status && this.props.user.permission < 3) {
            return;
        }
        this.setState(({ tokenLevels }) => ({
            tokenLevels: tokenLevels.map(level => {
                if (level._id === togglingLevel._id) {
                    if (level.isOpen) {
                        return {
                            ...level,
                            isOpen: false,
                        };
                    } else {
                        this.getTokenLevel(level);
                        return {
                            ...level,
                            isFetching: true,
                            isOpen: true,
                        };
                    }
                }
                return level;
            }),
        }));
    };
    saveToken = (savingToken, value) => {
        return new Promise(resolve => {
            this.props.post(
                `translation/edit-translation/${this.props.match.params.language}`,
                {
                    _id: savingToken._id,
                    value,
                },
                res => {
                    this.setState(
                        ({ tokenLevels }) => ({
                            tokenLevels: tokenLevels.map(level => {
                                return {
                                    ...level,
                                    ...(level.tokens && {
                                        tokens: level.tokens.map(token => ({
                                            ...token,
                                            translationToken:
                                                token._id === savingToken._id
                                                    ? res.data.translationToken
                                                    : token.translationToken,
                                        })),
                                    }),
                                };
                            }),
                        }),
                        () => {
                            resolve(true);
                        }
                    );
                },
                () => {
                    resolve(true);
                }
            );
        });
    };
    getToken = async refreshingToken => {
        this.props.get(
            `translation/token-translation/${this.props.match.params.language}/${refreshingToken._id}`,
            res => {
                this.setState(({ tokenLevels }) => ({
                    tokenLevels: tokenLevels.map(level => {
                        return {
                            ...level,
                            ...(level.tokens && {
                                tokens: level.tokens.map(token => ({
                                    ...token,
                                    translationToken:
                                        token._id === refreshingToken._id
                                            ? res.data.translationToken
                                            : token.translationToken,
                                })),
                            }),
                        };
                    }),
                }));
            }
        );
    };
    approveTranslation = approvingToken => {
        this.props.post(
            `translation/approve-translation/${this.props.match.params.language}`,
            { _id: approvingToken._id },
            res => {
                this.setState(({ tokenLevels }) => ({
                    tokenLevels: tokenLevels.map(level => {
                        return {
                            ...level,
                            ...(level.tokens && {
                                tokens: level.tokens.map(token => ({
                                    ...token,
                                    translationToken:
                                        token._id === approvingToken._id
                                            ? res.data.translationToken
                                            : token.translationToken,
                                })),
                            }),
                        };
                    }),
                }));
            }
        );
    };

    mergeLevel = (e, level) => {
        e.stopPropagation();
        this.props.post(
            `translation/merge/${this.props.match.params.language}`,
            {
                level: level._id,
            },
            () => {
                this.getTokenLevel(level);
            }
        );
    };

    changeSourceLanguage = ({ target: { value } }) => {
        store.set("sourceLanguage", value);
        this.setState(
            {
                sourceLanguage: value,
                tokenLevels: [],
                isInitalFetching: true,
            },
            this.getLevels
        );
    };

    handleLevelStatusChange = (e, _id, status) => {
        const {
            user,
        } = this.props;
        const { language } = this.props.match.params;
        if (user.permission > 2) {
            e.stopPropagation();
            this.props.post("set-token-level-status", {
                language,
                tokenLevel: _id,
                status: (status + 1) % 5,
            }, () => {
                this.setState({
                    translationLevelStatus:
                        this.state.translationLevelStatus.map(_status => {
                            if (_status.level === _id) {
                                return {
                                    ..._status,
                                    status: (status + 1) % 5,
                                };
                            }
                            return _status;
                        })
                })
            })
        }
    }
    handleGetDelta = () => {
        const { language } = this.props.match.params;
        this.props.get(`delta-json-for-translation/${language}`, (res) => {
            console.log(res);
            const tokens = res.data;
            this.handleGetTokens(tokens)
        })
    }
    handleGetTokens = (tokens, level: any = {}) => {
        let part = 0;
        const maxItem = 25;
        let totalTranslations = 0;
        const usedLanguages = ["hu", "en-us", "it", "lt", "ro", "nl"];
        while (true) {
            const messages = {};
            let lastPart = false;
            for (let t = part * maxItem; t < (part + 1) * maxItem; t++) {
                const token = tokens[t];
                if (!token) {
                    lastPart = true;
                    break;
                }
                const tokenObj = {};
                usedLanguages.forEach(lang => {
                    if (token[lang]) {
                        tokenObj[lang] = token[lang];
                    } else if (lang === 'en-us') {
                        tokenObj[lang] = token["en-gb"] || token["en"];
                    }
                });
                totalTranslations++;
                messages[token.key] = tokenObj;
            }
            const json = {
                category: level?.name || "Missing translations",
                categoryDescription: level?.description || "All messages which are not translated yet",
                messages,
                part
            }
            const fileName = level?._id ? `${level?.name}-part-${part}.json` : `${this.props.match.params.language}-delta-part-${part}.json`
            downloadJSON(json, fileName);
            part++;
            if (lastPart) break;
        }
    }
    handleDownloadJSON = (e, level) => {
        e.stopPropagation();

        this.props.get(`level-json-for-translation/${level._id}`, (res) => {
            console.log(res);
            const tokens = res.data;
            this.handleGetTokens(tokens, level)
        })
    }
    handleUploadJSON = async () => {
        const { uploadJSON } = this.state;
        this.props.post(`json-upload-from-ai/${this.props.match.params.language}`, JSON.parse(uploadJSON));
        this.setState({ uploadJSON: "" });
    }
    render() {
        const {
            intl: { formatMessage },
            user,
        } = this.props;
        const {
            isInitalFetching,
            tokenLevels,
            translationLevelStatus,
            users,
            progress,
            chatCheckedAt,
            sourceLanguage,
        } = this.state;
        const { language } = this.props.match.params;
        return (
            <div>
                <h1>{languages(formatMessage).find(({ value }) => value === language)?.label}</h1>
                <div>
                    <FormattedHTMLMessage id="translate.work.description" />
                </div>
                <Select
                    name="sourceLanguage"
                    label="Source language"
                    value={sourceLanguage}
                    options={[{ value: "en", label: "English" }, ...languages(formatMessage)]}
                    onChange={this.changeSourceLanguage}
                />
                {users.length > 0 && (
                    <TranslatorChat
                        setChatChecked={this.setChatChecked}
                        language={language}
                        users={users}
                        setEventSourceCallback={this.setEventSourceCallback}
                        chatCheckedAt={chatCheckedAt}
                    />
                )}
                {isInitalFetching && (
                    <LoaderWrapper>
                        <Loader />
                    </LoaderWrapper>
                )}

                {user.permission > 2 && (
                    <>
                        <InputTextArea
                            value={this.state.uploadJSON}
                            onChange={event => this.setState({ uploadJSON: event.currentTarget.value })}
                            label="Upload JSON"
                        />
                        <button onClick={this.handleUploadJSON} className="button">Upload</button>
                        <button onClick={this.handleGetDelta} className="button">Get Delta</button>
                    </>
                )}
                {!isInitalFetching && (
                    <div>
                        {tokenLevels.map(level => {
                            const status =
                                (translationLevelStatus.find(status => status.level === level._id) || { status: 0 })
                                    .status || 0;
                            return (
                                <Block key={level._id}>
                                    <BlockHeader
                                        onClick={() => this.toggleLevel(level)}
                                        isOpen={level.isOpen}
                                        style={{ cursor: !status ? "default" : "" }}
                                    >
                                        <LevelName>
                                            {level.name}{" "}
                                            {level.tokens ? (
                                                <>
                                                    ({level.tokens.filter(token => token.translationToken).length}/
                                                    {
                                                        level.tokens.filter(
                                                            token =>
                                                                token.translationToken &&
                                                                token.translationToken.approvedBy.length > 0
                                                        ).length
                                                    }
                                                    /{level.tokens.length})
                                                </>
                                            ) : (
                                                <>
                                                    ({progress.find(pr => pr._id === level._id).edited}/
                                                    {progress.find(pr => pr._id === level._id).approved}/
                                                    {progress.find(pr => pr._id === level._id).total}
                                                    {user.permission > 2 && progress.find(pr => pr._id === level._id).unmerged > 0 && (" - " + progress.find(pr => pr._id === level._id).unmerged)})
                                                </>
                                            )}
                                        </LevelName>
                                        {user.permission > 2 && (
                                            <MergeButton style={{ marginRight: 6 }} onClick={(e) => this.handleDownloadJSON(e, level)} className="button">
                                                JSON
                                            </MergeButton>
                                        )}
                                        {user.permission > 2 && (
                                            <MergeButton onClick={e => this.mergeLevel(e, level)} className="button">
                                                Merge
                                            </MergeButton>
                                        )}
                                        <StatusMarker levelStatus={status} onClick={(e) => this.handleLevelStatusChange(e, level._id, status)}>
                                            {translationLevelStatusLabels[status]}
                                        </StatusMarker>
                                        <Caret />
                                    </BlockHeader>
                                    {level.isOpen && (
                                        <BlockContent>
                                            {level.isFetching && (
                                                <LoaderWrapper>
                                                    <Loader />
                                                </LoaderWrapper>
                                            )}

                                            {!level.isFetching && (
                                                <TokenTable
                                                    tokens={level.tokens}
                                                    sourceLanguage={sourceLanguage}
                                                    saveToken={this.saveToken}
                                                    getToken={this.getToken}
                                                    users={users}
                                                    user={user}
                                                    approveTranslation={this.approveTranslation}
                                                    status={status}
                                                />
                                            )}
                                        </BlockContent>
                                    )}
                                </Block>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }
}

export default LanguagePage;
